import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { translations, type Translation } from '@/i18n/translations';
import type { Locale } from '@/lib/types';

type Theme = 'dark' | 'light';

interface PreferencesState {
  locale: Locale;
  theme: Theme;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/** Falls back to Spanish unless the browser clearly asks for English. */
function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'es';
  return navigator.language.toLowerCase().startsWith('en') ? 'en' : 'es';
}

function detectTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('light', theme === 'light');
  document.documentElement.style.colorScheme = theme;
}

export function applyLocale(locale: Locale): void {
  document.documentElement.lang = locale;
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set, get) => ({
      locale: detectLocale(),
      theme: detectTheme(),
      setLocale: (locale) => {
        applyLocale(locale);
        set({ locale });
      },
      toggleLocale: () => get().setLocale(get().locale === 'es' ? 'en' : 'es'),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
    }),
    {
      name: 'portfolio.preferences',
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        applyTheme(state.theme);
        applyLocale(state.locale);
      },
    }
  )
);

/** Shorthand hook: `const { t, locale } = useI18n()`. */
export function useI18n(): { t: Translation; locale: Locale } {
  const locale = usePreferences((state) => state.locale);
  return { t: translations[locale], locale };
}
